import i18n from "../i18n";
import useReaderStore from "../context/readerStore";
import {
  createTextboxApi,
  deleteTextboxApi,
  fetchAccessData,
  getSessionTokens,
  getValidRstToken,
  updateTextboxApi,
} from "./igpService";
import {
  showOperationFailedNotification,
  showSessionExpiredNotification,
  showFeatureNotAllowedNotification,
} from "./notificationService";
import { isFeatureAllowed } from "./drmPolicyService";

/**
 * Textbox (free-text annotation) service.
 *
 * Data model per textbox:
 * {
 *   id:          string   – unique id
 *   page_number: number   – 1-based PDF page
 *   content:     string   – user-entered text
 *   x:           number   – left offset as fraction of page width  (0–1)
 *   y:           number   – top  offset as fraction of page height (0–1)
 *   width:       number   – width  as fraction of page width  (0–1)
 *   height:      number   – height as fraction of page height (0–1)
 *   timestamp:   string   – ISO date
 * }
 *
 * Coordinates are stored as fractions (0–1) of the PDF page dimensions so
 * they survive zoom / scale changes.  The viewer converts to pixels at render
 * time using the current canvas size.
 *
 * Uses IGP API as source-of-truth, similar to highlights/underlines.
 */

export const DEFAULT_TEXTBOX_WIDTH = 0.12; // 12% of page width
export const DEFAULT_TEXTBOX_HEIGHT = 0.025; // 2.5% of page height – auto-grows

/** Generate a simple unique id */
const generateId = () =>
  `tb_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const normalizeTextbox = (tb) => {
  const coordinates = tb?.coordinates || {};
  return {
    ...tb,
    content: typeof tb?.content === "string" ? tb.content : "",
    x: Number(tb?.x ?? coordinates.left ?? 0),
    y: Number(tb?.y ?? coordinates.top ?? 0),
    width: Number(tb?.width ?? coordinates.width ?? DEFAULT_TEXTBOX_WIDTH),
    height: Number(tb?.height ?? coordinates.height ?? DEFAULT_TEXTBOX_HEIGHT),
  };
};

/* ------------------------------------------------------------------ */
/*  CRUD helpers                                                       */
/* ------------------------------------------------------------------ */

/**
 * Create a new textbox annotation.
 * @param {{ page_number: number, x: number, y: number, content?: string }} data
 * @returns {Object} the created textbox
 */
export async function refreshTextboxesFromApi() {
  try {
    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);
    const accessData = await fetchAccessData(grantId, rstToken, ["textbox"]);
    const textboxes = (accessData?.textbox?.data || []).map(normalizeTextbox);
    const { setAnnotationData } = useReaderStore.getState();
    setAnnotationData("textboxes", textboxes);
    return textboxes;
  } catch (err) {
    console.error("Error refreshing textboxes from API:", err);
    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    }
    return [];
  }
}

export async function loadTextboxes() {
  return refreshTextboxesFromApi();
}

export async function createTextbox(data) {
  const requestId = data.id || generateId();
  const { x, y, width, height, coordinates, ...rest } = data;
  const left = x ?? coordinates?.left ?? 0;
  const top = y ?? coordinates?.top ?? 0;
  const boxWidth = width ?? coordinates?.width ?? DEFAULT_TEXTBOX_WIDTH;
  const boxHeight = height ?? coordinates?.height ?? DEFAULT_TEXTBOX_HEIGHT;
  const timestamp = new Date().toISOString();

  // Optimistic UI update so textbox appears immediately on click.
  const optimisticTextbox = normalizeTextbox({
    ...rest,
    id: requestId,
    page_number: data.page_number,
    content: data.content || "",
    _isLocalDraft: true,
    timestamp,
    coordinates: {
      left,
      top,
      width: boxWidth,
      height: boxHeight,
    },
  });
  const { addAnnotationToStore } = useReaderStore.getState();
  addAnnotationToStore("textboxes", optimisticTextbox);

  return optimisticTextbox;
}

/**
 * Update a textbox (e.g. content or position).
 * @param {string} id
 * @param {Object} updates – partial textbox fields
 */
export async function updateTextbox(id, updates) {
  try {
    const currentTextboxes =
      useReaderStore.getState().annotations.textboxes?.data || [];
    const existing = currentTextboxes.find((tb) => tb.id === id) || {};
    if (!existing?.id) return [];

    // Never send blank textbox content to API.
    if (typeof updates.content === "string" && updates.content.trim() === "") {
      return await deleteTextbox(id);
    }

    const merged = {
      ...existing,
      ...updates,
    };

    const left = merged.x ?? merged.coordinates?.left ?? 0;
    const top = merged.y ?? merged.coordinates?.top ?? 0;
    const width =
      merged.width ?? merged.coordinates?.width ?? DEFAULT_TEXTBOX_WIDTH;
    const height =
      merged.height ?? merged.coordinates?.height ?? DEFAULT_TEXTBOX_HEIGHT;

    const {
      x: _x,
      y: _y,
      width: _width,
      height: _height,
      coordinates: _coordinates,
      _isLocalDraft,
      ...restMerged
    } = merged;

    const payload = {
      ...restMerged,
      page_number: merged.page_number ?? existing.page_number,
      content: merged.content ?? existing.content ?? "",
      coordinates: {
        left,
        top,
        width,
        height,
      },
    };

    // Keep draft textboxes local-only until user enters content.
    if (existing._isLocalDraft && updates.content === undefined) {
      const { updateAnnotationInStore } = useReaderStore.getState();
      updateAnnotationInStore("textboxes", id, payload);
      return useReaderStore.getState().annotations.textboxes?.data || [];
    }

    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);

    // Check DRM policy before attempting to create/update on backend
    const drmPolicy = useReaderStore.getState().drmPolicy;
    if (!isFeatureAllowed(drmPolicy, "textbox")) {
      showFeatureNotAllowedNotification("text_box");
      return useReaderStore.getState().annotations.textboxes?.data || [];
    }

    if (existing._isLocalDraft) {
      // First content save should create the textbox on backend.
      await createTextboxApi(grantId, rstToken, {
        ...payload,
        id,
      });
    } else {
      await updateTextboxApi(grantId, rstToken, id, payload);
    }
    return await refreshTextboxesFromApi();
  } catch (err) {
    console.error("Error updating textbox:", err);
    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(i18n.t("notifications.operations.updateTextbox"));
    }
    throw err;
  }
}

/**
 * Delete a textbox by id.
 * @param {string} id
 */
export async function deleteTextbox(id) {
  try {
    const currentTextboxes =
      useReaderStore.getState().annotations.textboxes?.data || [];
    const existing = currentTextboxes.find((tb) => tb.id === id) || {};
    if (!existing?.id) return [];

    if (existing._isLocalDraft) {
      const { removeAnnotationFromStore } = useReaderStore.getState();
      removeAnnotationFromStore("textboxes", id);
      return useReaderStore.getState().annotations.textboxes?.data || [];
    }

    const { grantId, agtToken } = getSessionTokens();
    const rstToken = await getValidRstToken(grantId, agtToken);

    const left = existing.x ?? existing.coordinates?.left ?? 0;
    const top = existing.y ?? existing.coordinates?.top ?? 0;
    const width =
      existing.width ?? existing.coordinates?.width ?? DEFAULT_TEXTBOX_WIDTH;
    const height =
      existing.height ?? existing.coordinates?.height ?? DEFAULT_TEXTBOX_HEIGHT;

    const {
      x: _x,
      y: _y,
      width: _width,
      height: _height,
      coordinates: _coordinates,
      ...restExisting
    } = existing;

    await deleteTextboxApi(grantId, rstToken, id, {
      ...restExisting,
      page_number: existing.page_number,
      coordinates: {
        left,
        top,
        width,
        height,
      },
    });
    return await refreshTextboxesFromApi();
  } catch (err) {
    console.error("Error deleting textbox:", err);
    if (err.response?.status === 401) {
      showSessionExpiredNotification();
    } else {
      showOperationFailedNotification(i18n.t("notifications.operations.deleteTextbox"));
    }
    throw err;
  }
}
